import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Fade,
  Slide,
  Zoom,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Parse from "parse";
import { getRoleBasedOptions } from "../utils/chatbotUtils";
import ReactMarkdown from "react-markdown";
import NewTicketDialog from "../Modals/Ticket/NewTicketDialog";
import MyTicketsDialog from "../Modals/Ticket/MyTicketsDialog";
import { useGetIdentity, useNotify } from "react-admin";

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasScrolledUp, setHasScrolledUp] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [currentStep, setCurrentStep] = useState("main");
  const [selectedMainOption, setSelectedMainOption] = useState(null);
  const [selectedSubOption, setSelectedSubOption] = useState(null);
  const [isTextFieldEnabled, setIsTextFieldEnabled] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [isCheckingSettings, setIsCheckingSettings] = useState(true);
  const [showSupportOption, setShowSupportOption] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [myTicketsDialogOpen, setMyTicketsDialogOpen] = useState(false);
  const chatContainerRef = useRef(null);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const inputRef = useRef(null);
  const role = identity?.userRoleName;

  const { mainOptions, subOptions, finalOptions, finalAnswer } =
    getRoleBasedOptions(role);

  // Check if chatbot is enabled from Settings
  useEffect(() => {
    const checkChatbotSettings = async () => {
      try {
        const query = new Parse.Query("Settings");
        query.equalTo("type", "chatbot");
        const settings = await query.first({ useMasterKey: true });

        if (settings) {
          const isEnabled = settings.get("settings")?.[0] === "true";
          setChatbotEnabled(isEnabled);
        } else {
          // If no settings found, default to disabled
          setChatbotEnabled(false);
        }
      } catch (error) {
        console.error("Error checking chatbot settings:", error);
        setChatbotEnabled(false);
      } finally {
        setIsCheckingSettings(false);
      }
    };

    checkChatbotSettings();
  }, []);

  useEffect(() => {
    if (isOpen && chat.length === 0) {
      if (mainOptions.length === 0) {
        setIsLoading(true);
        scrollToBottom();
        setChat([]);

        setTimeout(() => {
          setIsLoading(false);

          // Show customer support option after answering
          setShowSupportOption(true);
          setCurrentStep("support");
          setSelectedMainOption(null);
          setSelectedSubOption(null);

          if (role !== "Super-User") {
            setTimeout(() => {
              setChat((prev) => [
                ...prev,
                {
                  role: "assistant",
                  text: "Need more help? You can contact our Customer Support team:",
                },
              ]);
              setShowOptions(true);
            }, 300);
          } else {
            setCurrentStep("main");
            setShowOptions(true);
          }
        }, 500);
      } else {
        scrollToBottom();
        setChat([
          {
            role: "assistant",
            text: "Hello! How can I assist you today? Please select a category:",
          },
        ]);
        setCurrentStep("main");
        setShowOptions(true);
        setIsTextFieldEnabled(false);
      }
    }
  }, [isOpen, chat.length, mainOptions, role]);

  useEffect(() => {
    if (chatContainerRef.current && !hasScrolledUp) {
      scrollToBottom();
    } else if (
      isOpen &&
      chat.length > 0 &&
      chat[chat.length - 1].role === "assistant"
    ) {
      setUnreadCount((prev) => prev + 1);
    }
  }, [chat, isOpen, hasScrolledUp]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 300);
    }
  }, [isOpen]);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 20;

    setHasScrolledUp(!isAtBottom);
    setShowScrollButton(!isAtBottom);

    if (isAtBottom) {
      setUnreadCount(0);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
      setHasScrolledUp(false);
      setShowScrollButton(false);
      setUnreadCount(0);
    }
  };

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  const clearChat = () => {
    if (mainOptions.length === 0) {
      setIsLoading(true);
      scrollToBottom();
      setChat([]);

      setTimeout(() => {
        setIsLoading(false);

        // Show customer support option after answering
        setShowSupportOption(true);
        setCurrentStep("support");

        if (role === "Super-User") {
          setCurrentStep("main");
        }
        setShowOptions(true);
      }, 500);
    } else {
      setChat([
        {
          role: "assistant",
          text: "Hello! How can I assist you today? Please select a category:",
        },
      ]);
      setCurrentStep("main");
      setShowSupportOption(false);
    }
    setSelectedMainOption(null);
    setSelectedSubOption(null);
    setShowOptions(true);
    setShowScrollButton(false);
    setIsTextFieldEnabled(false);
    setUnreadCount(0);
  };

  const handleMainOptionSelect = (option) => {
    setShowOptions(false);

    if (option.id === "other") {
      setIsTextFieldEnabled(true);
      setCurrentStep("other");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setIsTextFieldEnabled(false);
      setSelectedMainOption(option.id);
      setCurrentStep("sub");
      setChat((prev) => [
        ...prev,
        { role: "user", text: option.label },
        {
          role: "assistant",
          text: `You've selected ${option.label}. Please choose a specific topic:`,
        },
      ]);
      setShowOptions(true);
    }
  };

  const handleSubOptionSelect = (option) => {
    setShowOptions(false);
    setChat((prev) => [...prev, { role: "user", text: option.label }]);
    setSelectedSubOption(option.id);
    setCurrentStep("final");
    setChat((prev) => [
      ...prev,
      {
        role: "assistant",
        text: `Let's narrow it down further. What would you like to know about ${option.label}?`,
      },
    ]);
    setShowOptions(true);
  };

  const handleFinalOptionSelect = async (option) => {
    setShowOptions(false);
    setChat((prev) => [...prev, { role: "user", text: option }]);

    const answer =
      finalAnswer[option] ||
      "I don't have information on that specific topic yet. Please contact support for assistance.";

    setIsLoading(true);
    scrollToBottom();

    setTimeout(() => {
      setChat((prev) => [...prev, { role: "assistant", text: answer }]);
      setIsLoading(false);

      // Show customer support option after answering
      setShowSupportOption(true);
      setCurrentStep("support");
      setSelectedMainOption(null);
      setSelectedSubOption(null);

      if (role !== "Super-User") {
        setTimeout(() => {
          setChat((prev) => [
            ...prev,
            {
              role: "assistant",
              text: "Need more help? You can contact our Customer Support team:",
            },
          ]);
          setShowOptions(true);
        }, 300);
      } else {
        setCurrentStep("main");
        setShowOptions(true);
      }
    }, 500);
  };

  const handleSupportOptionSelect = (option) => {
    if (option === "New Ticket") {
      setTicketDialogOpen(true);
      setIsOpen(false);
    } else if (option === "My Tickets") {
      setMyTicketsDialogOpen(true);
      setIsOpen(false);
    }
  };

  const handleTicketSubmit = async (ticketData) => {
    try {
      const userId = identity?.userId;
      const role = identity?.userRoleName;

      if (!userId) {
        notify("User not authenticated. Please login again.", {
          type: "error",
        });
        return;
      }

      const ticketPayload = {
        userId: userId,
        category: ticketData.category,
        description: ticketData.description,
        role: role,
      };

      let attachments = [];

      if (ticketData.fileBase64) {
        const data = {};
        data.fileBase64 = ticketData.fileBase64;
        data.fileName = ticketData.fileName;
        data.fileType = ticketData.fileType;
        data.fileSize = ticketData.fileSize;
        attachments.push(data);
      }

      ticketPayload.attachments = attachments;

      const result = await Parse.Cloud.run("createTicket", ticketPayload);

      if (result.success) {
        notify("Ticket created successfully!", { type: "success" });
        console.log("Ticket created:", result.data);
      } else {
        notify(result.message || "Failed to create ticket", { type: "error" });
      }
    } catch (error) {
      console.error("Error submitting ticket:", error);
      notify(error.message || "An error occurred while creating the ticket", {
        type: "error",
      });
    }
  };

  const sendMessageToBackend = async (message) => {
    setIsLoading(true);
    scrollToBottom();

    try {
      const res = await Parse.Cloud.run("chatbot", {
        role: role,
        message: message,
        conversationHistory: chat,
      });

      setTimeout(() => {
        if (res.success) {
          setChat((prev) => [...prev, { role: "assistant", text: res.data }]);
        } else {
          setChat((prev) => [
            ...prev,
            {
              role: "assistant",
              text:
                "⚠️ " +
                (res.message || "Something went wrong. Please try again."),
            },
          ]);
        }
        setIsLoading(false);
      }, 500);
    } catch (e) {
      setTimeout(() => {
        setChat((prev) => [
          ...prev,
          {
            role: "assistant",
            text: "⚠️ Could not reach chatbot service. Please check your connection and try again.",
          },
        ]);
        setIsLoading(false);
      }, 500);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMsg = { role: "user", text: input };
    setChat((prev) => [...prev, userMsg]);
    setInput("");
    await sendMessageToBackend(input);
    if (!isTextFieldEnabled) {
      setCurrentStep("main");
    }
    setSelectedMainOption(null);
    setSelectedSubOption(null);
    setShowSupportOption(false);
    setShowOptions(true);
  };

  const TypingIndicator = () => (
    <Box sx={{ display: "flex", gap: 0.5, p: 0.5 }}>
      <Zoom in={true} style={{ transitionDelay: "0ms" }}>
        <Box
          sx={{
            width: 4,
            height: 4,
            bgcolor: "#FFC107",
            borderRadius: "50%",
          }}
          className="typing-dot"
        />
      </Zoom>
      <Zoom in={true} style={{ transitionDelay: "200ms" }}>
        <Box
          sx={{
            width: 4,
            height: 4,
            bgcolor: "#FFC107",
            borderRadius: "50%",
          }}
          className="typing-dot"
        />
      </Zoom>
      <Zoom in={true} style={{ transitionDelay: "400ms" }}>
        <Box
          sx={{
            width: 4,
            height: 4,
            bgcolor: "#FFC107",
            borderRadius: "50%",
          }}
          className="typing-dot"
        />
      </Zoom>
    </Box>
  );

  // Don't render anything if chatbot is disabled or still checking
  if (isCheckingSettings || !chatbotEnabled) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 1300,
        "& .MuiPaper-root": {
          transition: "all 0.3s ease-in-out",
        },
      }}
    >
      {isOpen ? (
        <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
          <Paper
            elevation={8}
            sx={{
              width: { xs: "95vw", sm: 400 },
              height: { xs: "80vh", sm: 500 },
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              bgcolor: "#242424",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 1.5,
                background: "#1C1C1E",
                color: "white",
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Zoom in={true}>
                  <ChatIcon sx={{ mr: 1.5 }} />
                </Zoom>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Game Admin Support
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Clear conversation">
                  <IconButton
                    onClick={clearChat}
                    size="small"
                    sx={{
                      color: "white",
                      opacity: 0.8,
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close chat">
                  <IconButton
                    onClick={toggleChat}
                    size="small"
                    sx={{
                      color: "white",
                      ml: 1,
                      "&:hover": {
                        transform: "rotate(90deg)",
                        transition: "transform 0.3s",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box
              ref={chatContainerRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                p: 2.5,
                overflowY: "auto",
                bgcolor: "#1A1A1A",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                position: "relative",
                scrollBehavior: "smooth",
              }}
            >
              {chat.map((msg, index) => (
                <Fade key={index} in={true} timeout={500}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems:
                        msg.role === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "90%",
                        pb: 1,
                        px: 1.5,
                        borderRadius:
                          msg.role === "user"
                            ? "18px 18px 4px 18px"
                            : "18px 18px 18px 4px",
                        bgcolor: msg.role === "user" ? "#FFC107" : "#242424",
                        color: msg.role === "user" ? "black" : "white",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                        position: "relative",
                        "&:hover": {
                          boxShadow: "0 3px 6px rgba(0,0,0,0.4)",
                          transform: "translateY(-1px)",
                          transition: "all 0.2s",
                        },
                        fontSize: 12,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 12,
                          "& p, & ul, & ol, & pre, & code, & blockquote": {
                            marginBottom: 0,
                            paddingBottom: 0,
                          },
                        }}
                      >
                        <ReactMarkdown>{msg.text}</ReactMarkdown>
                      </Typography>
                    </Box>
                  </Box>
                </Fade>
              ))}

              {showOptions && (
                <Fade in={true}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      justifyContent: "flex-start",
                    }}
                  >
                    {currentStep === "main" &&
                      mainOptions.map((option) => (
                        <Button
                          key={option.id}
                          variant="contained"
                          size="small"
                          onClick={() => handleMainOptionSelect(option)}
                          sx={{
                            borderRadius: 2,
                            fontSize: 12,
                            textTransform: "none",
                            bgcolor: "#242424",
                            color: "white",
                            border: "1px solid #444",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                            "&:hover": {
                              bgcolor: "#FFC107",
                              color: "black",
                              borderColor: "#FFC107",
                            },
                          }}
                        >
                          {option.icon} {option.label}
                        </Button>
                      ))}
                    {currentStep === "sub" &&
                      subOptions[selectedMainOption]?.map((option) => (
                        <Button
                          key={option.id}
                          variant="contained"
                          size="small"
                          onClick={() => handleSubOptionSelect(option)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            bgcolor: "#242424",
                            color: "white",
                            border: "1px solid #444",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                            "&:hover": {
                              bgcolor: "#FFC107",
                              color: "black",
                              borderColor: "#FFC107",
                            },
                          }}
                        >
                          {option.icon} {option.label}
                        </Button>
                      ))}
                    {currentStep === "final" &&
                      finalOptions[selectedSubOption]?.map((option, index) => (
                        <Button
                          key={index}
                          variant="contained"
                          size="small"
                          onClick={() => handleFinalOptionSelect(option)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            bgcolor: "#242424",
                            color: "white",
                            border: "1px solid #444",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                            "&:hover": {
                              bgcolor: "#FFC107",
                              color: "black",
                              borderColor: "#FFC107",
                            },
                            width: "100%",
                            justifyContent: "flex-start",
                            textAlign: "left",
                            py: 1,
                          }}
                        >
                          {option}
                        </Button>
                      ))}
                    {currentStep === "support" &&
                      role !== "Super-User" &&
                      showSupportOption && (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() =>
                              handleSupportOptionSelect("New Ticket")
                            }
                            sx={{
                              borderRadius: 2,
                              fontSize: 12,
                              textTransform: "none",
                              bgcolor: "#242424",
                              color: "white",
                              border: "1px solid #444",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                              "&:hover": {
                                bgcolor: "#FFC107",
                                color: "black",
                                borderColor: "#FFC107",
                              },
                              width: "48%",
                            }}
                          >
                            🎫 New Ticket
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() =>
                              handleSupportOptionSelect("My Tickets")
                            }
                            sx={{
                              borderRadius: 2,
                              fontSize: 12,
                              textTransform: "none",
                              bgcolor: "#242424",
                              color: "white",
                              border: "1px solid #444",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                              "&:hover": {
                                bgcolor: "#FFC107",
                                color: "black",
                                borderColor: "#FFC107",
                              },
                              width: "48%",
                            }}
                          >
                            📋 My Tickets
                          </Button>
                        </>
                      )}
                  </Box>
                </Fade>
              )}

              {isLoading && (
                <Fade in={true}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      mt: 1,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 2,
                        bgcolor: "#242424",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                      }}
                    >
                      <TypingIndicator />
                    </Box>
                  </Box>
                </Fade>
              )}

              {showScrollButton && (
                <Zoom in={true}>
                  <Box
                    sx={{
                      position: "sticky",
                      bottom: 10,
                      alignSelf: "center",
                      zIndex: 2,
                    }}
                  >
                    <Tooltip title="Scroll to bottom">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={scrollToBottom}
                        sx={{
                          bgcolor: "#FFC107",
                          color: "black",
                          boxShadow: 2,
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 3,
                            bgcolor: "#FFB300",
                          },
                          transition: "all 0.2s",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Zoom>
              )}
            </Box>

            <Divider />

            {isTextFieldEnabled && (
              <Box sx={{ p: 1.5, bgcolor: "#242424" }}>
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendMessage();
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#1A1A1A",
                    borderRadius: 1.5,
                    px: 1,
                    border: "1px solid #444",
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    inputRef={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && !e.shiftKey && sendMessage()
                    }
                    placeholder="Type a message..."
                    disabled={isLoading}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: 12,
                        padding: 0,
                        color: "white",
                      },
                      "& .MuiInputBase-root": {
                        p: 1,
                      },
                    }}
                  />
                  <Tooltip title="Send message">
                    <span>
                      <IconButton
                        onClick={sendMessage}
                        disabled={isLoading || !input.trim()}
                        sx={{
                          ml: 1,
                          color: "#FFC107",
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "scale(1.1)",
                            bgcolor: "#FFC107",
                            color: "black",
                          },
                          "&.Mui-disabled": {
                            color: "#666",
                          },
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress
                            size={24}
                            thickness={4}
                            sx={{ color: "#FFC107" }}
                          />
                        ) : (
                          <SendIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Paper>
        </Slide>
      ) : (
        <Zoom in={!isOpen} timeout={500}>
          <Box>
            <IconButton
              aria-label="Open chat"
              sx={{
                bgcolor: "#FFC107",
                color: "black",
                borderRadius: "50%",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                width: 56,
                height: 56,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1) rotate(10deg)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
                  bgcolor: "#FFB300",
                },
                "@keyframes pulse": {
                  "0%": {
                    boxShadow: "0 0 0 0 rgba(255, 193, 7, 0.4)",
                  },
                  "70%": {
                    boxShadow: "0 0 0 15px rgba(255, 193, 7, 0)",
                  },
                  "100%": {
                    boxShadow: "0 0 0 0 rgba(255, 193, 7, 0)",
                  },
                },
                animation: unreadCount > 0 ? "pulse 2s infinite" : "none",
              }}
              onClick={toggleChat}
            >
              <ChatIcon fontSize="large" />
            </IconButton>
          </Box>
        </Zoom>
      )}

      <Box
        sx={{
          "@keyframes bounce": {
            "0%, 100%": {
              transform: "translateY(0)",
            },
            "50%": {
              transform: "translateY(-5px)",
            },
          },
          "& .typing-dot": {
            animation: "bounce 1.4s infinite ease-in-out both",
          },
          "& .typing-dot:nth-of-type(1)": {
            animationDelay: "0s",
          },
          "& .typing-dot:nth-of-type(2)": {
            animationDelay: "0.2s",
          },
          "& .typing-dot:nth-of-type(3)": {
            animationDelay: "0.4s",
          },
        }}
      />

      {/* New Ticket Dialog */}
      <NewTicketDialog
        open={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        onSubmit={handleTicketSubmit}
      />

      {/* My Tickets Dialog */}
      <MyTicketsDialog
        open={myTicketsDialogOpen}
        onClose={() => setMyTicketsDialogOpen(false)}
      />
    </Box>
  );
};

export default ChatbotWidget;
